/**
 * Env-aware logger — replaces raw `console.*` calls so production builds can
 * silence non-error noise while keeping warnings/errors intact for diagnostics.
 *
 * How it works
 * ------------
 * - In development (NODE_ENV !== "production") every level passes through to
 *   the real console — same DX as before.
 * - In production `logger.debug` and `logger.log` become no-ops. `logger.warn`
 *   and `logger.error` still fire so real problems reach the browser console.
 * - Every call is prefixed with `[VitaQMS]` to make console grepping obvious.
 * - `warn` and `error` are ALSO broadcast to `POST /api/logs` (best-effort,
 *   throttled) so admins see real-user problems in the admin dashboard without
 *   needing an external observability provider.
 *
 * Usage
 * -----
 *   import { logger } from "@/lib/logger";
 *   logger.warn("auth: /auth/me returned", err?.response?.status);
 *
 * Rationale
 * ---------
 * The code review flagged 43 raw `console.*` calls as production noise. This
 * lets us keep the informational logs during development while stripping them
 * in prod without swallowing real errors — the shotgun "remove all console
 * calls" fix the reviewer suggested would have silenced legitimate warnings.
 */

const IS_PROD = process.env.NODE_ENV === "production";
const TAG = "[VitaQMS]";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---- Remote log broadcast (best-effort, throttled) --------------------------
// Coalesces bursts (e.g. a broken API endpoint that fires on every render) into
// one POST per (level + message) key every RECENT_WINDOW_MS. This means the
// admin dashboard sees the issue exactly once — not 200 times a second.

const RECENT_WINDOW_MS = 30_000;
const recent = new Map(); // key -> last-sent-timestamp

const _stringifyArg = (a) => {
  if (a instanceof Error) return `${a.name}: ${a.message}`;
  if (typeof a === "object" && a !== null) {
    try { return JSON.stringify(a).slice(0, 500); } catch { return "[unserialisable]"; }
  }
  return String(a);
};

const _broadcast = (level, args) => {
  try {
    const message = args.map(_stringifyArg).join(" ").slice(0, 2000);
    const key = `${level}::${message}`;
    const now = Date.now();
    const last = recent.get(key);
    if (last && (now - last) < RECENT_WINDOW_MS) return; // throttled
    recent.set(key, now);
    // Occasional Map cleanup so a long-lived tab doesn't leak memory.
    if (recent.size > 100) {
      for (const [k, t] of recent) if (now - t > RECENT_WINDOW_MS * 4) recent.delete(k);
    }
    // Fire-and-forget. `keepalive` lets errors flush during page unload.
    fetch(`${API}/logs`, {
      method: "POST",
      credentials: "include",
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        level,
        message,
        url: window.location.href,
        user_agent: navigator.userAgent,
      }),
    }).catch(() => { /* swallow — we do NOT want log-broadcast failures to log again */ });
  } catch {
    // Never throw from the logger.
  }
};

const noop = () => {};

export const logger = {
  // Suppressed in production — dev-only diagnostic output.
  debug: IS_PROD ? noop : (...args) => console.debug(TAG, ...args),
  log:   IS_PROD ? noop : (...args) => console.log(TAG, ...args),
  info:  IS_PROD ? noop : (...args) => console.info(TAG, ...args),
  // Always fires — production issues need to surface both to console and admin.
  warn:  (...args) => { console.warn(TAG, ...args); _broadcast("warn", args); },
  error: (...args) => { console.error(TAG, ...args); _broadcast("error", args); },
};

export default logger;
